import React, { useState } from 'react';
import { Button, Menu, MenuItem, Box, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FaChevronDown } from 'react-icons/fa';
import { useHistory } from 'react-router-dom';
import { ShipyardContext } from '../Context';
import { useTranslation } from 'react-i18next';
import { comparisonHelper } from '../protected/moveRecordPage/MoveRecordPage';
import { toast } from 'react-toastify';

const useStyles = makeStyles(() => ({
    button: {
        ...buttonStyle,
    },
    icon: {
        color: '#FFFFFF',
        marginLeft: '18px',
        borderLeft: '2px solid #FFFFFF',
        paddingLeft: '18px',
    },
}));

const ActionDropdown = ({ selectedItem, listExitCallback, ctx }) => {
    const { setMoveConfig, resetMoveConfig } = React.useContext(ShipyardContext);
    const [anchorEl, setAnchorEl] = useState(null);
    const history = useHistory();
    const classes = useStyles();
    const [t] = useTranslation();

    React.useEffect(() => {
        resetMoveConfig();
    }, []);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    //These are the move options for when a user is trying to move an application.
    const moveOptions = {
        dataKey: 'Unique ID',
        label1: t('MoveRecord.Application.StudentToMoveTitle'),
        label2: t('MoveRecord.Application.DestinationStudentTitle'),
        description1: t('MoveRecord.Application.StudentToMoveDescription'),
        description2: t('MoveRecord.Application.DestinationStudentDescription'),
        moveViewTitle: t('MoveRecord.Application.ViewTitle'),
        inputPlaceholder: t('MoveRecord.Application.SearchPlaceholder'),
        possibleMatchesLanguage: t('MoveRecord.Application.PossibleMatches'),
        sort: '`Applicant First Name` ASC',
        getURL: (id) => `/admin/application_instance/${id}/move_candidates`,
        updateURL: (id) => `/admin/application_instance/${id}/move`,
        buildBody: (target, toRecord) => {
            return {
                FromUserID: target.UserID,
                ToUserID: toRecord.UserID,
                UniqueID: comparisonHelper(target, toRecord).selectedRecordToMerge.id,
                AppTypeID: comparisonHelper(target, toRecord).target.typeID,
            };
        },
        renderModalContent: () => null,
        cancelText: 'No, Cancel Move',
        confirmText: 'Yes, Move Record',
        modalTitle: 'Do you want to complete the move?',
        buildComparison: (target, toRecord, modalContentList) => {
            return (
                <ul>
                    <li style={modalContentList}>
                        Move {comparisonHelper(target, toRecord)?.target?.fullName}’s ({target.ID}) application for the {target.SY} SY to{' '}
                        {comparisonHelper(target, toRecord)?.selectedRecordToMerge?.fullName} (ID{' '}
                        {comparisonHelper(target, toRecord)?.selectedRecordToMerge?.id}){' '}
                    </li>
                    <li style={modalContentList}>
                        Grant access to {comparisonHelper(target, toRecord)?.target?.fullName}’s ({target.ID}) application for the{' '}
                        {target.SY} SY to {comparisonHelper(target, toRecord)?.selectedRecordToMerge?.guardianFullName} (User ID{' '}
                        {comparisonHelper(target, toRecord)?.selectedRecordToMerge?.email})
                    </li>
                    <li style={modalContentList}>
                        Move all history, annual statement(s), and invoice data associated with{' '}
                        {comparisonHelper(target, toRecord)?.target?.fullName} ({target.ID}) {target.SY} application to{' '}
                        {comparisonHelper(target, toRecord)?.selectedRecordToMerge?.fullName} (ID{' '}
                        {comparisonHelper(target, toRecord)?.selectedRecordToMerge?.id}).
                    </li>
                    <li style={modalContentList}>
                        Delete student {comparisonHelper(target, toRecord)?.target?.fullName} ({target.ID}).
                    </li>
                </ul>
            );
        },
        buildConfirmDescription: (target, toRecord, modalContentOverride) => {
            return (
                <>
                    <Typography sx={{ fontWeight: 700, color: (theme) => theme.palette.error.main, fontSize: 20 }}>WARNING:</Typography>
                    <Typography sx={modalContentOverride}>
                        The action you are about to take is final and cannot be undone. Are you sure you want to attach{' '}
                        {comparisonHelper(target, toRecord)?.target?.fullName}’s (ID {target?.ID}) {target?.SY}
                        application data, history, annual statements, and invoices to the chosen student (
                        {comparisonHelper(target, toRecord)?.selectedRecordToMerge?.fullName} - ID{' '}
                        {comparisonHelper(target, toRecord)?.selectedRecordToMerge?.id})?
                    </Typography>
                </>
            );
        },
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const moveRecordHandler = () => {
        if (selectedItem?.size === 1) {
            let [_item] = selectedItem;
            listExitCallback('move');
            setMoveConfig({ ...moveOptions, applicationInstanceId: _item?.ApplicationInstanceId });
            history.push('/actions/move-record');
        } else {
            toast.error(selectedItem?.size > 1 ? 'Only one record can be moved at a time.' : 'Please select a record to move.');
        }
    };

    const batchUpdateHandler = () => {
        let _arr = Array.from(selectedItem);
        if (_arr?.length > 0) {
            listExitCallback('batch_update');
            handleClose();
        } else {
            toast.error('Please select one or more records to update in batch.');
        }
    };

    const reportHandler = () => {
        let _arr = Array.from(selectedItem);
        if (_arr?.length > 0) {
            listExitCallback('report');
            handleClose();
        } else {
            toast.error('Please select one or more records to include in the report.');
        }
    };

    return (
        <Box
            display="flex"
            justifyContent="flex-end"
        >
            <Button
                onClick={handleClick}
                className={classes.button}
            >
                Select Actions
                <span className={classes.icon}>
                    <FaChevronDown />
                </span>
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                sx={{
                    '& .MuiPaper-root': {
                        border: '1px solid #354DA2',
                    },
                    '& .MuiMenuItem-root:hover': {
                        backgroundColor: '#E9EFFF',
                        color: '#494FF9',
                    },
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem onClick={() => batchUpdateHandler()}>{ctx === 'application' ? 'Update selected applications' : "Batch Update"}</MenuItem>
                
                {ctx === 'application' ? <MenuItem onClick={() => moveRecordHandler()}>Move Record</MenuItem> : null}

                {ctx === 'application' ? <MenuItem onClick={() => reportHandler()}>Export Data</MenuItem> : null}
            </Menu>
        </Box>
    );
};

export default ActionDropdown;

export const buttonStyle = {
    backgroundColor: '#014E8D',
    color: '#FFFFFF',
    '&:hover': {
        backgroundColor: '#014E8D',
    },
    fontFamily: 'Roboto',
};
